<template>
  <div
    v-if="showOptions"
    class="message-options__container"
    data-dd-action-name="message_options"
  >
    <HDropdown
      icon="icChevronArrowDown"
      :options="dropdown"
      option-key="text"
      position="right"
      size="small"
      :data-theme="whiteChevron ? 'on-dark' : 'on-light'"
      @select="trackOptionsClick"
    >
      <template #item="{ item }">
        <div class="message-options__item" :class="{ disabled: item.disabled }">
          <WIcon
            :icon="item.icon"
            size="medium"
            class="message-options__item-icon"
          />
          {{ item.text }}
        </div>
      </template>
    </HDropdown>
  </div>
</template>

<script>
import { WIcon } from '@alice-health/wonderland-vue'

import { HDropdown } from '@/components/shared/atoms'
import { MessageTypes, REPLY_MESSAGE_TYPES } from '@/models/channels'
import Logger, { EventModel, EventName } from '@/wrappers/logger'

export default {
  components: {
    WIcon,
    HDropdown
  },
  inject: ['confirm', 'notify'],
  props: {
    isFromCurrentUser: Boolean,
    isFromMember: Boolean,
    isPrivate: Boolean,
    message: { type: Object, default: undefined },
    channelId: { type: String, default: undefined },
    channelPersonId: { type: String, default: undefined },
    fileLink: { type: String, default: undefined },
    memberName: { type: String, default: undefined }
  },
  emits: ['close'],
  computed: {
    whiteChevron() {
      return this.isFromMember || this.isPrivate
    },
    isImage() {
      return this.message.type === MessageTypes.IMAGE
    },
    showOptions() {
      return this.dropdown.length > 0
    },
    shouldReply() {
      return (
        !this.isFromCurrentUser &&
        REPLY_MESSAGE_TYPES.includes(this.message.type)
      )
    },
    dropdown() {
      const dropdown = []

      const replyOption = this.getReplyOption()
      if (replyOption) dropdown.push(replyOption)

      if (this.isImage && this.fileLink && !this.isFromCurrentUser) {
        dropdown.push({
          icon: 'icPicture',
          text: 'Salvar arquivo como...',
          onClick: this.showTestResult
        })
      }
      if (
        this.isFromCurrentUser &&
        !this.message.deletedAt &&
        !this.isPrivate
      ) {
        dropdown.push({
          icon: 'icTrash',
          text: 'Apagar Mensagem',
          onClick: this.deleteMessage
        })
      }
      return dropdown
    }
  },
  methods: {
    getReplyOption() {
      if (this.shouldReply) {
        return {
          icon: 'icArrowUpperLeft',
          text: 'Responder',
          onClick: this.replyMessage
        }
      }
      return null
    },
    trackOptionsClick(value) {
      if (value) {
        Logger.addLog(EventName.USER_INTERACTION_MESSAGE, {
          model: EventModel.CHANNELS,
          data: {
            context: 'open_options'
          }
        })
      }
    },
    track(data) {
      Logger.addLog(EventName.USER_INTERACTION_MESSAGE, {
        model: EventModel.CHANNELS,
        data
      })
    },
    async deleteMessage() {
      try {
        this.track({ context: 'delete_message', action: 'open' })

        const confirmed = await this.confirm({
          title: 'Esta ação é irreversível',
          warning: true,
          description: `Você deseja mesmo apagar a mensagem que enviou para ${this.memberName}?`,
          ok: 'Apagar mensagem'
        })

        if (!confirmed) {
          this.track({ context: 'delete_message', action: 'close' })
          return
        }

        const body = await this.$store.dispatch(
          'pillars/channel/message/deleteMessage',
          {
            channelId: this.channelId,
            messageId: this.message.id
          }
        )

        this.track({ context: 'delete_message', action: 'delete' })

        if (body.id !== this.message.id) {
          this.notify({
            description:
              'Ocorreu um erro ao apagar a mensagem, por favor tente novamente!',
            duration: 6000
          })
        } else {
          this.$emit('close')
        }
      } catch (error) {
        this.notify({
          description:
            'Ocorreu um erro ao apagar a mensagem, por favor tente novamente!',
          duration: 6000
        })
      }
    },
    showTestResult() {
      const { content: file, id: messageId } = this.message

      this.track({ context: 'test_result_modal', action: 'open' })
      this.$store.commit('ui/channel/toggleTestResultModal')
      this.$store.commit('pillars/channel/setUploadTestResultImageData', {
        channelId: this.channelId,
        channelPersonId: this.channelPersonId,
        file,
        messageId
      })
    },
    replyMessage() {
      this.track({ context: 'reply_message', action: 'reply' })
      this.$store.dispatch('pillars/channel/message/setReplyTo', this.message)
    }
  }
}
</script>

<style lang="scss">
@import '@/style/_non-rendering';
.message-options {
  &__container {
    @extend %body-small;
    position: absolute;
    right: 0;
    top: 0;
    justify-content: flex-end;
    z-index: 5;
    color: var(--gl-color-shades-gray-100);
    display: inline-flex;
  }

  &__cta {
    min-width: 28px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--gl-spacing-01);
    &.disabled {
      color: var(--gl-color-shades-gray-30);
      cursor: not-allowed;
    }
  }
}
</style>
